import { Card, CardContent } from "@repo/o37-ui";
import { TrendingUp, DollarSign } from "lucide-react";
import { formatCents } from '../../types';

interface RunRateWidgetProps {
  monthlyRunRate: number;
  annualRunRate: number;
  activeSubscriptions: number;
  topCategories: { category: string; amount: number }[];
}

export default function RunRateWidget({
  monthlyRunRate,
  annualRunRate,
  activeSubscriptions,
  topCategories
}: RunRateWidgetProps) {
  // Calculate percentage for each category
  const totalMonthly = topCategories.reduce((sum, c) => sum + c.amount, 0);

  // Color palette for categories
  const colors = [
    'bg-blue-500',
    'bg-emerald-500',
    'bg-purple-500',
    'bg-amber-500',
    'bg-rose-500',
    'bg-cyan-500',
    'bg-indigo-500',
    'bg-orange-500'
  ];

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold">Run Rate Analysis</h2>
          <div className="flex items-center gap-1 text-sm text-neutral-500">
            <TrendingUp className="w-4 h-4" />
            <span>{activeSubscriptions} active subscriptions</span>
          </div>
        </div>

        {/* Run Rate Display */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
            <div className="flex items-center gap-2 text-blue-600 mb-2">
              <DollarSign className="w-5 h-5" />
              <span className="text-sm font-medium">Monthly Run Rate</span>
            </div>
            <div className="text-3xl font-bold text-blue-900">
              {formatCents(monthlyRunRate)}
            </div>
            <div className="text-sm text-blue-600 mt-1">per month</div>
          </div>

          <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl">
            <div className="flex items-center gap-2 text-emerald-600 mb-2">
              <DollarSign className="w-5 h-5" />
              <span className="text-sm font-medium">Annual Run Rate</span>
            </div>
            <div className="text-3xl font-bold text-emerald-900">
              {formatCents(annualRunRate)}
            </div>
            <div className="text-sm text-emerald-600 mt-1">per year</div>
          </div>
        </div>

        {/* Category Breakdown */}
        {topCategories.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-neutral-700 mb-3">Cost by Category</h3>

            {/* Progress Bar */}
            <div className="h-3 bg-neutral-100 rounded-full overflow-hidden flex mb-4">
              {topCategories.map((cat, i) => {
                const percentage = totalMonthly > 0 ? (cat.amount / totalMonthly) * 100 : 0;
                return (
                  <div
                    key={cat.category}
                    className={`${colors[i % colors.length]} h-full`}
                    style={{ width: `${percentage}%` }}
                    title={`${cat.category}: ${formatCents(cat.amount)} (${percentage.toFixed(1)}%)`}
                  />
                );
              })}
            </div>

            {/* Legend */}
            <div className="grid grid-cols-2 gap-2">
              {topCategories.map((cat, i) => {
                const percentage = totalMonthly > 0 ? (cat.amount / totalMonthly) * 100 : 0;
                return (
                  <div key={cat.category} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${colors[i % colors.length]}`} />
                      <span className="text-neutral-600 truncate">{cat.category}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-neutral-900">{formatCents(cat.amount)}</span>
                      <span className="text-neutral-400">({percentage.toFixed(0)}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {topCategories.length === 0 && (
          <div className="text-center py-4 text-neutral-500">
            <p>No subscription data yet</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
